import * as React from 'react';
import { LogIn, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * O localizador nas duas transferências — entre localizadores e entre
 * centros. Sempre pelo CÓDIGO (a descrição se repete no cadastro, o código
 * não); o que salta na tela é a descrição achada, que é o que a pessoa confere
 * com o olho na etiqueta da prateleira.
 */

export interface Posicao {
  localizador: number;
  endereco: string;
  ativo: boolean;
}

export function CampoLocalizador({
  ponta,
  valor,
  aoMudar,
  resolvido,
  carregando,
  rotulo,
  dica = 'opcional — digite o código do localizador',
  id: idProprio,
}: {
  ponta: 'origem' | 'destino';
  valor: string;
  aoMudar: (v: string) => void;
  resolvido: Posicao | string | null;
  carregando: boolean;
  /** Padrão: "Sai de" / "Vai para". */
  rotulo?: string;
  /** O que aparece com o campo vazio. */
  dica?: string;
  id?: string;
}): JSX.Element {
  const ok = resolvido !== null && typeof resolvido === 'object' && !carregando;
  const ruim = typeof resolvido === 'string' && !carregando;
  const id = idProprio ?? `localizador-${ponta}`;
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col justify-center gap-1 rounded-xl border px-3 py-2 transition-colors',
        ok ? 'border-primaria/40 bg-primaria-suave/40' : ruim ? 'border-perigo/40 bg-perigo/[0.05]' : 'border-borda bg-superficie2',
      )}
    >
      <label htmlFor={id} className="rotulo">
        {rotulo ?? (ponta === 'origem' ? 'Sai de' : 'Vai para')}
      </label>
      <div className="flex min-w-0 items-center gap-2.5">
        <input
          id={id}
          value={valor}
          onChange={(e) => aoMudar(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          placeholder="código"
          aria-label={ponta === 'origem' ? 'Código do localizador de origem' : 'Código do localizador de destino'}
          className="tabular h-8 w-[88px] shrink-0 rounded-md border border-borda bg-superficie px-2 text-center font-mono text-[12.5px] focus:border-primaria focus:outline-none focus:ring-2 focus:ring-primaria/15"
        />
        <span className="min-w-0 flex-1" aria-live="polite">
          {valor.trim() === '' ? (
            <span className="text-[12px] text-textoFraco">{dica}</span>
          ) : carregando ? (
            <span className="text-[12px] text-textoFraco">procurando…</span>
          ) : ok ? (
            <span className="block truncate font-mono text-[17px] font-bold leading-tight tracking-[-0.01em] text-texto" title={resolvido.endereco}>
              {resolvido.endereco}
              {!resolvido.ativo ? (
                <span className="ml-1.5 align-middle font-sans text-[11px] font-semibold text-atencao">
                  inativo
                </span>
              ) : null}
            </span>
          ) : ruim ? (
            <span className="block text-[12px] leading-tight text-perigo">{resolvido}</span>
          ) : null}
        </span>
      </div>
    </div>
  );
}

/**
 * Um localizador na grade, com a seta da perna: SAINDO (vermelho, como a saída
 * do kardex) na origem e ENTRANDO (verde) no destino. A descrição vem em
 * destaque — é ela que se confere na prateleira; o código vai miúdo ao lado.
 */
export function Localizador({
  p,
  ponta,
  textoCru,
  extra,
}: {
  p: Posicao | null;
  ponta: 'origem' | 'destino';
  /** O que veio na linha quando o localizador não foi achado. */
  textoCru?: string | null;
  extra?: React.ReactNode;
}): JSX.Element {
  const Icone = ponta === 'origem' ? LogOut : LogIn;
  return (
    <span className="flex min-w-0 items-start gap-1.5">
      <Icone
        className={cn(
          'mt-0.5 h-3.5 w-3.5 shrink-0',
          !p ? 'text-textoSuave' : ponta === 'origem' ? 'text-perigo' : 'text-sucesso',
        )}
        aria-label={ponta === 'origem' ? 'sai de' : 'entra em'}
      />
      {p ? (
        <span className="min-w-0 leading-tight">
          <span className="block truncate font-mono font-bold" title={p.endereco}>
            {p.endereco}
          </span>
          <span className="tabular block font-mono text-[10.5px] text-textoFraco">
            {p.localizador}
            {extra}
          </span>
        </span>
      ) : (
        <span className="font-mono text-textoFraco">{textoCru ?? '—'}</span>
      )}
    </span>
  );
}
